'use client';

import { type ComponentProps, type SyntheticEvent, useEffect, useRef } from 'react';
import { handleField } from '@/lib/utils/handleField';
import {
  clearLegacyInstagramHandle,
  legacyInstagramHandle,
  writeLastInstagramHandle,
} from '@/lib/utils/lastInstagramHandle';

/**
 * Shows what is typed or pasted as `@handle` (`handleField`). `focusEmpty` only on a fine pointer:
 * on a phone the keyboard would cover the page before it is read.
 */
export function HandleInput({
  handle,
  focusEmpty,
  ...props
}: ComponentProps<'input'> & { handle: string | null; focusEmpty?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);

  const tidy = (event: SyntheticEvent<HTMLInputElement>) => {
    // Mid-composition the keyboard owns the text: rewriting it duplicates letters on Android.
    if ((event.nativeEvent as InputEvent).isComposing) return;
    const el = event.currentTarget;
    const shown = handleField(el.value);
    if (shown === el.value) return;
    // Keep the caret where it was, counted from the end, so an edit mid-word stays put.
    const fromEnd = el.value.length - (el.selectionEnd ?? el.value.length);
    el.value = shown;
    const at = Math.max(0, shown.length - fromEnd);
    el.setSelectionRange(at, at);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (handle) {
      clearLegacyInstagramHandle();
    } else {
      // Every field on the page reads it, so it is only cleared once the server fills them.
      const legacy = legacyInstagramHandle();
      if (legacy) writeLastInstagramHandle(legacy);
      // Never over what they started typing before the script landed.
      if (legacy && !el.value) el.value = handleField(legacy);
    }
    if (focusEmpty && !el.value && window.matchMedia('(pointer: fine)').matches) {
      el.focus({ preventScroll: true });
    }
  }, [handle, focusEmpty]);

  return (
    <input
      ref={ref}
      defaultValue={handle ? `@${handle}` : undefined}
      onInput={tidy}
      onCompositionEnd={tidy}
      {...props}
    />
  );
}
